import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  FormControlLabel,
  Checkbox,
  FormGroup,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationOnIcon,
  Edit as EditIcon,
  DateRange as DateRangeIcon,
  DirectionsCar as DirectionsCarIcon
} from '@mui/icons-material';
import { auth } from '../../../../../firebase';
import { PROCESSING_ENDPOINTS } from '../../constants/serviceEndpoints';
import { getApiBase, debugApiConfig, getCurrentEnvironment } from '../../../../../utils/apiConfig';
import SchoolSearchInput from '../shared/SchoolSearchInput';

const SchoolReprocessor = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolIdentifier, setSchoolIdentifier] = useState(''); // Keep for backward compatibility
  const [servicesToReprocess, setServicesToReprocess] = useState({
    esri: false,
    metrics: false,
    nearby_schools: false,
    projections: false,
    ai_summaries: true
  });
  
  // Location update options
  const [updatedAddress, setUpdatedAddress] = useState('');
  const [updatedCoordinates, setUpdatedCoordinates] = useState({
    latitude: '',
    longitude: ''
  });
  const [useCoordinates, setUseCoordinates] = useState(false);
  
  // Name update options
  const [updatedName, setUpdatedName] = useState('');

  // Data year options
  const [dataYear, setDataYear] = useState('2024');
  const [schoolYear, setSchoolYear] = useState('2024-25');

  // District update options
  const [updatedDistrictName, setUpdatedDistrictName] = useState('');
  const [clearDistrictName, setClearDistrictName] = useState(false);

  // ESRI travel configuration
  const ALL_DRIVE_TIMES = [5, 10, 15, 20, 25, 30];
  const [selectedDriveTimes, setSelectedDriveTimes] = useState([5, 10, 15]);
  const [travelMode, setTravelMode] = useState('drive');
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [reprocessingResults, setReprocessingResults] = useState(null);
  // AI summary background status: null | 'generating' | 'complete' | 'timeout'
  const [aiSummaryStatus, setAiSummaryStatus] = useState(null);

  // Reordered services to reflect logical dependency flow
  const availableServices = [
    { 
      key: 'esri', 
      label: 'ESRI Demographics', 
      description: 'Refresh demographic data',
      autoRequiredBy: 'location change'
    },
    { 
      key: 'nearby_schools', 
      label: 'Nearby Schools', 
      description: 'Update nearby schools analysis',
      requires: ['esri']
    },
    { 
      key: 'projections', 
      label: 'Enrollment Projections', 
      description: 'Regenerate enrollment forecasts'
    },
    { 
      key: 'metrics', 
      label: 'School Metrics', 
      description: 'Recalculate performance and trend metrics',
      requiredBy: ['esri', 'nearby_schools', 'projections']
    },
    { 
      key: 'ai_summaries', 
      label: 'AI Summaries', 
      description: 'Regenerate AI-powered analysis',
      requires: ['metrics']
    }
  ];

  const DEFAULT_DRIVE_TIMES = [5, 10, 15];

  const handleServiceToggle = (serviceKey) => {
    setServicesToReprocess(prev => {
      const newState = { ...prev };
      const isCurrentlySelected = prev[serviceKey];

      if (isCurrentlySelected) {
        // Trying to deselect - check if anything depends on it
        if (serviceKey === 'esri') {
          if (newState.nearby_schools || newState.metrics) {
            alert('Cannot deselect ESRI Demographics while Nearby Schools or Metrics are selected. Deselect those first.');
            return prev;
          }
          const hasExtendedTimes = selectedDriveTimes.some(t => !DEFAULT_DRIVE_TIMES.includes(t));
          if (hasExtendedTimes || travelMode === 'walk') {
            alert('ESRI Demographics is required when extended drive times or walk mode are selected. Reset the travel configuration to defaults first.');
            return prev;
          }
        }
        if (serviceKey === 'nearby_schools' && newState.metrics) {
          alert('Cannot deselect Nearby Schools while Metrics is selected. Deselect Metrics first.');
          return prev;
        }
        if (serviceKey === 'projections' && newState.metrics && !newState.esri && !newState.nearby_schools) {
          alert('Cannot deselect Projections while Metrics is selected (and no other dependencies are selected). Deselect Metrics first.');
          return prev;
        }
        // Allow deselection
        newState[serviceKey] = false;
      } else {
        // Selecting - apply dependency rules
        newState[serviceKey] = true;
        
        if (serviceKey === 'esri') {
          // Auto-select Nearby Schools and Metrics when ESRI is selected
          newState.nearby_schools = true;
          newState.metrics = true;
        } else if (serviceKey === 'nearby_schools') {
          // Auto-select Metrics when Nearby Schools is selected
          newState.metrics = true;
        } else if (serviceKey === 'projections') {
          // Auto-select Metrics when Projections is selected
          newState.metrics = true;
        }
      }
      
      return newState;
    });
  };

  // Auto-select ESRI when location changes
  useEffect(() => {
    const hasLocationChange = (useCoordinates && updatedCoordinates.latitude && updatedCoordinates.longitude) || 
                              (!useCoordinates && updatedAddress.trim());
    
    if (hasLocationChange && !servicesToReprocess.esri) {
      // Auto-select ESRI (which will auto-select nearby_schools and metrics)
      setServicesToReprocess(prev => ({
        ...prev,
        esri: true,
        nearby_schools: true,
        metrics: true
      }));
    }
  }, [updatedAddress, updatedCoordinates.latitude, updatedCoordinates.longitude, useCoordinates, servicesToReprocess.esri]);

  // Auto-select ESRI (and its cascade) when extended drive times or walk mode are
  // chosen. The database won't have polygon / demographic data for those values yet,
  // so ESRI must run first to produce it.
  useEffect(() => {
    const hasExtendedTimes = selectedDriveTimes.some(t => !DEFAULT_DRIVE_TIMES.includes(t));
    const isWalkMode = travelMode === 'walk';

    if ((hasExtendedTimes || isWalkMode) && !servicesToReprocess.esri) {
      setServicesToReprocess(prev => ({
        ...prev,
        esri: true,
        nearby_schools: true,
        metrics: true
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriveTimes, travelMode]);

  // Poll for AI summary completion when ai_summaries was requested and reprocessing succeeded
  useEffect(() => {
    const uuid = reprocessingResults?.school_uuid;
    const wasRequested = servicesToReprocess.ai_summaries;
    if (!uuid || !wasRequested || !reprocessingResults?.overall_success) return;

    // Use the reprocess completion timestamp so we only count summaries that
    // were freshly generated by this run, not pre-existing ones.
    const reprocessedAt = reprocessingResults?.reprocessing_completed_at
      ? new Date(reprocessingResults.reprocessing_completed_at)
      : new Date();

    setAiSummaryStatus('generating');
    let done = false;

    const checkStatus = async () => {
      if (done) return;
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(
          `${getApiBase()}/v2/schools/${uuid}/ai-summary/status`,
          { headers, credentials: 'include' }
        );
        if (resp.ok) {
          const data = await resp.json();
          // A summary counts as "new" only if its generated_at is after the
          // moment the reprocess HTTP response was returned.
          const hasNewSummaries = Object.values(data.status || {}).some(byType =>
            Object.values(byType).some(
              s => s?.generated_at && new Date(s.generated_at) > reprocessedAt
            )
          );
          if (hasNewSummaries) {
            done = true;
            setAiSummaryStatus('complete');
            clearInterval(intervalId);
            clearTimeout(timeoutId);
          }
        }
      } catch (err) {
        console.warn('AI summary status check failed:', err);
      }
    };

    const intervalId = setInterval(checkStatus, 15000);
    const timeoutId = setTimeout(() => {
      if (!done) {
        clearInterval(intervalId);
        setAiSummaryStatus('timeout');
      }
    }, 600000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [reprocessingResults?.school_uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSchoolSelect = (school) => {
    setSelectedSchool(school);
    // Update schoolIdentifier for backward compatibility with existing logic
    if (school) {
      setSchoolIdentifier(school.uuid);
    } else {
      setSchoolIdentifier('');
    }
  };

  const handleReprocess = async () => {
    if (!selectedSchool && !schoolIdentifier.trim()) {
      alert('Please select a school from the search results');
      return;
    }

    const selectedServices = Object.keys(servicesToReprocess).filter(key => servicesToReprocess[key]);
    const hasNameUpdate = updatedName.trim();
    const hasLocationUpdate = (useCoordinates && updatedCoordinates.latitude && updatedCoordinates.longitude) || 
                             (!useCoordinates && updatedAddress.trim());
    const hasDistrictUpdate = updatedDistrictName.trim() || clearDistrictName;
    
    // Allow reprocessing if there are services selected OR if there are name/location/district updates
    if (selectedServices.length === 0 && !hasNameUpdate && !hasLocationUpdate && !hasDistrictUpdate) {
      alert('Please select at least one service to reprocess OR provide a name/location/district update');
      return;
    }

          setIsProcessing(true);
      setReprocessingResults(null);
      setAiSummaryStatus(null);
      setProcessingStatus('Preparing reprocessing request...');

    try {
      // Debug API configuration in development/staging
      if (getCurrentEnvironment() !== 'production') {
        debugApiConfig();
      }
      
      const authHeaders = await getAuthHeaders();
      
      const requestBody = {
        school_uuid: selectedSchool ? selectedSchool.uuid : schoolIdentifier.trim(),
        services: selectedServices
      };

      // Add location updates if provided
      if (useCoordinates && updatedCoordinates.latitude && updatedCoordinates.longitude) {
        requestBody.updated_coordinates = {
          latitude: parseFloat(updatedCoordinates.latitude),
          longitude: parseFloat(updatedCoordinates.longitude)
        };
      } else if (!useCoordinates && updatedAddress.trim()) {
        requestBody.updated_address = updatedAddress.trim();
      }

      // Add name update if provided
      if (updatedName.trim()) {
        requestBody.updated_name = updatedName.trim();
      }

      // Add data year and school year if provided
      if (dataYear && dataYear !== '2023') {
        requestBody.data_year = parseInt(dataYear);
      }
      if (schoolYear && schoolYear !== '2024-25') {
        requestBody.school_year = schoolYear;
      }

      // Add district update if provided
      if (updatedDistrictName.trim()) {
        requestBody.updated_district_name = updatedDistrictName.trim();
      }
      if (clearDistrictName) {
        requestBody.clear_district_name = true;
      }

      // Add travel configuration when ESRI, AI summaries, or nearby schools are selected
      const needsTravelConfig = servicesToReprocess.esri || servicesToReprocess.ai_summaries || servicesToReprocess.nearby_schools;
      if (needsTravelConfig) {
        requestBody.drive_times = selectedDriveTimes;
        requestBody.travel_mode = travelMode;
      }

      // DEVELOPMENT OVERRIDE: Force reprocessing to use local backend
      // since we're developing locally but might have staging environment variables set
      const isLocalDevelopment = process.env.NODE_ENV === 'development' || 
                                window.location.hostname === 'localhost' ||
                                window.location.hostname === '127.0.0.1';
      
      let apiUrl;
      if (isLocalDevelopment || process.env.REACT_APP_FORCE_LOCAL_REPROCESS) {
        // Use local backend for development
        apiUrl = `http://localhost:5001/api${PROCESSING_ENDPOINTS.REPROCESSING}`;
      } else {
        // Use standard environment routing for production
        apiUrl = `${getApiBase()}${PROCESSING_ENDPOINTS.REPROCESSING}`;
      }
      
      console.log(`Making reprocessing request to: ${apiUrl}`);
      console.log('Environment check - isDev:', isLocalDevelopment, 'NODE_ENV:', process.env.NODE_ENV);
      console.log('Request body:', requestBody);
      console.log('Auth headers:', Object.keys(authHeaders));

      setProcessingStatus('Sending request to backend...');
      
      // Create an AbortController for timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 900000); // 15 minute timeout for reprocessing

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      setProcessingStatus('Processing response...');

      console.log('Response status:', response.status, response.statusText);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        // Handle different types of error responses
        const contentType = response.headers.get('content-type');
        let errorMessage = `Reprocessing failed: ${response.status} ${response.statusText}`;
        
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (jsonError) {
            console.error('Failed to parse error response as JSON:', jsonError);
          }
        } else {
          // Handle HTML error responses (like 404 pages)
          const responseText = await response.text();
          console.error('Non-JSON error response:', responseText.substring(0, 200));
          
          if (responseText.includes('<!doctype') || responseText.includes('<html')) {
            errorMessage = `Server returned HTML instead of JSON. This usually indicates a routing problem. Status: ${response.status}`;
          } else {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setProcessingStatus('Reprocessing completed successfully!');
      setReprocessingResults(result);

    } catch (error) {
      console.error('Reprocessing failed:', error);
      
      let errorMessage = 'Reprocessing failed';
      if (error.name === 'AbortError') {
        errorMessage = 'Reprocessing timed out after 15 minutes. The process may still be running in the background.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Network error during reprocessing. Please check your connection and try again.';
      } else {
        errorMessage = error.message;
      }
      
      setReprocessingResults({
        overall_success: false,
        error: errorMessage,
        reprocessing_completed_at: new Date().toISOString()
      });
    } finally {
      setIsProcessing(false);
      if (!reprocessingResults) {
        setProcessingStatus('');
      }
    }
  };

  const getAuthHeaders = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.warn('No authenticated user found');
      return {};
    }
    try {
      const token = await currentUser.getIdToken();
      return { 'Authorization': `Bearer ${token}` };
    } catch (error) {
      console.error('Error getting auth token:', error);
      return {};
    }
  };

  // Removed getApiBase() - now using centralized apiConfig

  const renderServiceResult = (serviceKey, result) => {
    const service = availableServices.find(s => s.key === serviceKey);
    const isSuccess = result?.success;

    return (
      <Box key={serviceKey} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {isSuccess ? (
            <CheckCircleIcon color="success" />
          ) : (
            <ErrorIcon color="error" />
          )}
          <Typography variant="h6">{service?.label}</Typography>
        </Box>
        
        {result?.error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {result.error}
          </Alert>
        )}
        
        {result?.data_summary && (
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {JSON.stringify(result.data_summary, null, 2)}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderNameTab = () => (
    <Stack spacing={3}>
      <Typography variant="body1" color="text.secondary">
        Update the school's display name. This will create a new name record while preserving the original name for reference.
      </Typography>
      
      <TextField
        label="New School Name"
        value={updatedName}
        onChange={(e) => setUpdatedName(e.target.value)}
        placeholder="Enter the new school name..."
        disabled={isProcessing}
        fullWidth
        multiline={false}
        inputProps={{ 
          maxLength: 100 
        }}
        helperText={`${updatedName.length}/100 characters. Leave empty to keep current name.`}
      />
      
      {selectedSchool && (
        <Box sx={{ 
          p: 2, 
          bgcolor: 'grey.50', 
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200'
        }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current school name:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
            {selectedSchool.name || 'No name available'}
          </Typography>
        </Box>
      )}
    </Stack>
  );

  const renderLocationTab = () => (
    <Stack spacing={3}>
      <FormControlLabel
        control={
          <Checkbox
            checked={useCoordinates}
            onChange={(e) => setUseCoordinates(e.target.checked)}
            disabled={isProcessing}
          />
        }
        label="Use coordinates instead of address"
      />

      {useCoordinates ? (
        <Stack spacing={2}>
          <TextField
            label="Latitude"
            value={updatedCoordinates.latitude}
            onChange={(e) => setUpdatedCoordinates(prev => ({ ...prev, latitude: e.target.value }))}
            placeholder="40.7128"
            type="number"
            inputProps={{ step: 'any' }}
            disabled={isProcessing}
            fullWidth
          />
          <TextField
            label="Longitude"
            value={updatedCoordinates.longitude}
            onChange={(e) => setUpdatedCoordinates(prev => ({ ...prev, longitude: e.target.value }))}
            placeholder="-74.0060"
            type="number"
            inputProps={{ step: 'any' }}
            disabled={isProcessing}
            fullWidth
          />
        </Stack>
      ) : (
        <TextField
          label="Updated Address"
          value={updatedAddress}
          onChange={(e) => setUpdatedAddress(e.target.value)}
          placeholder="123 Main St, City, State 12345"
          disabled={isProcessing}
          fullWidth
          helperText="Leave empty to keep current location"
        />
      )}
    </Stack>
  );

  const renderDataYearAndDistrictTab = () => (
    <Stack spacing={3}>
      <Typography variant="body1" color="text.secondary">
        Set the data year and school year for the reprocessing, and optionally update the district name.
      </Typography>
      
      {/* Data Year Section */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Data Year Settings
        </Typography>
        
        <Stack spacing={2}>
          <TextField
            label="Data Year"
            value={dataYear}
            onChange={(e) => {
              const year = e.target.value;
              setDataYear(year);
              // Auto-calculate school year when data year changes
              if (year && !isNaN(year)) {
                const nextYear = parseInt(year) + 1;
                setSchoolYear(`${year}-${nextYear.toString().slice(-2)}`);
              }
            }}
            placeholder="2024"
            type="number"
            inputProps={{ 
              min: 2019,
              max: 2030,
              step: 1
            }}
            disabled={isProcessing}
            helperText="Year for the data records (default: 2024)"
            sx={{ maxWidth: 200 }}
          />
          
          <TextField
            label="School Year"
            value={schoolYear}
            onChange={(e) => setSchoolYear(e.target.value)}
            placeholder="2024-25"
            disabled={isProcessing}
            helperText="Academic year format (e.g., 2024-25)"
            sx={{ maxWidth: 200 }}
          />
        </Stack>
      </Box>

      <Divider />

      {/* District Section */}
      <Box>
        <Typography variant="h6" gutterBottom>
          District Information
        </Typography>
        
        {selectedSchool && selectedSchool.lea_name && (
          <Box sx={{ 
            p: 2, 
            bgcolor: 'grey.50', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
            mb: 2
          }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current district name:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              {selectedSchool.lea_name}
            </Typography>
          </Box>
        )}
        
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={clearDistrictName}
                onChange={(e) => {
                  setClearDistrictName(e.target.checked);
                  if (e.target.checked) {
                    setUpdatedDistrictName('');
                  }
                }}
                disabled={isProcessing}
              />
            }
            label="Clear district name (set to NULL)"
          />
          
          {!clearDistrictName && (
            <TextField
              label="New District Name"
              value={updatedDistrictName}
              onChange={(e) => setUpdatedDistrictName(e.target.value)}
              placeholder="Enter new district name..."
              disabled={isProcessing || clearDistrictName}
              fullWidth
              inputProps={{ 
                maxLength: 100 
              }}
              helperText={`${updatedDistrictName.length}/100 characters. Leave empty to keep current district name.`}
            />
          )}
        </Stack>
      </Box>
    </Stack>
  );

  const renderServicesTab = () => {
    // Helper to check if a service is auto-selected due to dependencies
    const isAutoSelected = (serviceKey) => {
      if (serviceKey === 'nearby_schools') {
        return servicesToReprocess.esri;
      }
      if (serviceKey === 'metrics') {
        return servicesToReprocess.esri || servicesToReprocess.nearby_schools || servicesToReprocess.projections;
      }
      return false;
    };

    const getDependencyInfo = (service) => {
      const parts = [];
      
      if (service.key === 'esri' && ((useCoordinates && updatedCoordinates.latitude && updatedCoordinates.longitude) || 
          (!useCoordinates && updatedAddress.trim()))) {
        parts.push('Required by location change');
      }
      
      if (service.key === 'nearby_schools' && servicesToReprocess.esri) {
        parts.push('Auto-selected (ESRI is selected)');
      }
      
      if (service.key === 'metrics') {
        const reasons = [];
        if (servicesToReprocess.esri) reasons.push('ESRI');
        if (servicesToReprocess.nearby_schools && !servicesToReprocess.esri) reasons.push('Nearby Schools');
        if (servicesToReprocess.projections) reasons.push('Projections');
        if (reasons.length > 0) {
          parts.push(`Auto-selected (${reasons.join(', ')} selected)`);
        }
      }
      
      return parts.length > 0 ? parts.join(' • ') : null;
    };

    return (
      <Stack spacing={3}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
            Dependency Rules:
          </Typography>
          <Typography variant="body2" component="div">
            • Location changes automatically select ESRI<br/>
            • ESRI automatically selects Nearby Schools + Metrics<br/>
            • Nearby Schools automatically selects Metrics<br/>
            • Projections automatically selects Metrics<br/>
            • Metrics can be selected independently<br/>
            • Extended drive times or walk mode (set in <strong>ESRI Config</strong> tab) automatically select ESRI
          </Typography>
        </Alert>
        
        <FormGroup>
          {availableServices.map((service) => {
            const dependencyInfo = getDependencyInfo(service);
            const autoSelected = isAutoSelected(service.key);

            return (
              <FormControlLabel
                key={service.key}
                control={
                  <Checkbox
                    checked={servicesToReprocess[service.key]}
                    onChange={() => handleServiceToggle(service.key)}
                    disabled={isProcessing}
                    color={autoSelected ? "success" : "primary"}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: servicesToReprocess[service.key] ? 500 : 400 }}>
                      {service.label}
                      {autoSelected && (
                        <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1, fontStyle: 'italic' }}>
                          (auto)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {service.description}
                    </Typography>
                    {dependencyInfo && (
                      <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                        {dependencyInfo}
                      </Typography>
                    )}
                  </Box>
                }
              />
            );
          })}
        </FormGroup>

      </Stack>
    );
  };

  const renderEsriConfigTab = () => {
    const hasExtendedTimes = selectedDriveTimes.some(t => !DEFAULT_DRIVE_TIMES.includes(t));
    const isWalkMode = travelMode === 'walk';
    const isNonDefault = hasExtendedTimes || isWalkMode;

    return (
      <Stack spacing={3}>
        <Alert severity={isNonDefault ? 'warning' : 'info'}>
          <Typography variant="body2">
            {isNonDefault
              ? 'Non-default settings detected. ESRI Demographics, Nearby Schools, and Metrics will be automatically selected on the Services tab.'
              : 'Configure the travel mode and time rings used when collecting ESRI demographic data and computing nearby school polygons. Selecting extended times or walk mode will automatically enable the required services.'
            }
          </Typography>
        </Alert>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Travel Mode
          </Typography>
          <ToggleButtonGroup
            value={travelMode}
            exclusive
            onChange={(_, val) => { if (val) setTravelMode(val); }}
            disabled={isProcessing}
            size="small"
          >
            <ToggleButton value="drive">Drive</ToggleButton>
            <ToggleButton value="walk">Walk</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Travel Times (minutes)
          </Typography>
          <FormGroup row>
            {ALL_DRIVE_TIMES.map((t) => (
              <FormControlLabel
                key={t}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedDriveTimes.includes(t)}
                    disabled={isProcessing}
                    onChange={() => {
                      setSelectedDriveTimes(prev =>
                        prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t].sort((a, b) => a - b)
                      );
                    }}
                  />
                }
                label={<Typography variant="body2">{t} min</Typography>}
              />
            ))}
          </FormGroup>
          {selectedDriveTimes.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {servicesToReprocess.ai_summaries
                ? `Will generate ${4 * selectedDriveTimes.length} summaries (4 types × ${selectedDriveTimes.length} time${selectedDriveTimes.length !== 1 ? 's' : ''})`
                : `Will process ${selectedDriveTimes.length} time ring${selectedDriveTimes.length !== 1 ? 's' : ''}`
              }
            </Typography>
          )}
        </Box>
      </Stack>
    );
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <SettingsIcon color="primary" />
          <Typography variant="h5">School Reprocessing</Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Reprocess existing schools with updated data or refresh specific services.
        </Typography>

        {!auth.currentUser && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            You must be logged in to reprocess schools.
          </Alert>
        )}

        <Stack spacing={3}>
          {/* School Search Input */}
          <SchoolSearchInput
            value={selectedSchool}
            onChange={handleSchoolSelect}
            disabled={isProcessing}
            label="Search School for Reprocessing"
            placeholder="Type school name to search..."
            helperText="Search for schools in your teams - only shows schools available for reprocessing"
          />

          {/* Configuration Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab 
                label="Services" 
                icon={<RefreshIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="School Name" 
                icon={<EditIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Location Update" 
                icon={<LocationOnIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Data Year & District" 
                icon={<DateRangeIcon />} 
                iconPosition="start"
              />
              <Tab
                label="ESRI Config"
                icon={<DirectionsCarIcon />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          <Box sx={{ mt: 2 }}>
            {tabValue === 0 && renderServicesTab()}
            {tabValue === 1 && renderNameTab()}
            {tabValue === 2 && renderLocationTab()}
            {tabValue === 3 && renderDataYearAndDistrictTab()}
            {tabValue === 4 && renderEsriConfigTab()}
          </Box>

          {/* Reprocess Button */}
          <Stack spacing={1} sx={{ width: '100%' }}>
            <Button
              variant="contained"
              onClick={handleReprocess}
              disabled={(() => {
                // Must have a school selected
                if (!selectedSchool && !schoolIdentifier.trim()) return true;
                
                // Must be logged in
                if (!auth.currentUser) return true;
                
                // Can't process while already processing
                if (isProcessing) return true;
                
                // Check if any services are selected
                const hasServices = servicesToReprocess.esri || 
                                    servicesToReprocess.metrics || 
                                    servicesToReprocess.nearby_schools || 
                                    servicesToReprocess.projections;
                
                // Check if any updates are provided
                const hasNameUpdate = updatedName.trim() !== '';
                const hasLocationUpdate = (useCoordinates && updatedCoordinates.latitude && updatedCoordinates.longitude) || 
                                         (!useCoordinates && updatedAddress.trim() !== '');
                const hasDistrictUpdate = updatedDistrictName.trim() !== '' || clearDistrictName;
                
                // Enable if we have services OR any update
                const hasChanges = hasServices || hasNameUpdate || hasLocationUpdate || hasDistrictUpdate;
                
                return !hasChanges;
              })()}
              startIcon={isProcessing ? <CircularProgress size={20} /> : <RefreshIcon />}
              size="large"
              fullWidth
            >
              {isProcessing ? 'Reprocessing...' : 'Update School'}
            </Button>
            
            {/* Helper text for disabled button */}
            {!isProcessing && (!selectedSchool && !schoolIdentifier.trim()) && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                Please select a school first
              </Typography>
            )}
            {!isProcessing && (selectedSchool || schoolIdentifier.trim()) && 
             !servicesToReprocess.esri && !servicesToReprocess.metrics && 
             !servicesToReprocess.nearby_schools && !servicesToReprocess.projections &&
             !updatedName.trim() && !updatedAddress.trim() && 
             !(updatedCoordinates.latitude && updatedCoordinates.longitude) &&
             !updatedDistrictName.trim() && !clearDistrictName && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                Please select at least one service or provide an update (name, location, or district)
              </Typography>
            )}
          </Stack>

          {/* Processing Status */}
          {isProcessing && processingStatus && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2">{processingStatus}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This process may take up to 15 minutes depending on the services selected.
              </Typography>
            </Alert>
          )}

          {/* Results Display */}
          {reprocessingResults && (
            <Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Reprocessing Results
              </Typography>

              <Alert 
                severity={reprocessingResults.overall_success ? 'success' : 'error'} 
                sx={{ mb: 2 }}
              >
                {reprocessingResults.overall_success ? 
                  'Reprocessing completed successfully!' : 
                  `Reprocessing failed: ${reprocessingResults.error || 'Unknown error'}`
                }
              </Alert>

              {aiSummaryStatus && (
                <Box
                  sx={{
                    mt: -1, mb: 2, p: 1.5,
                    bgcolor: aiSummaryStatus === 'complete' ? 'success.50' : 'grey.100',
                    borderRadius: 1,
                    display: 'flex', alignItems: 'flex-start', gap: 1.5
                  }}
                >
                  {aiSummaryStatus === 'generating' && (
                    <>
                      <CircularProgress size={18} sx={{ mt: 0.2, flexShrink: 0 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          AI Summaries generating in background
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {`${4 * selectedDriveTimes.length} summaries (4 types × ${selectedDriveTimes.length} drive time${selectedDriveTimes.length !== 1 ? 's' : ''}) are being generated. This typically takes 5–10 minutes. This page will update automatically.`}
                        </Typography>
                      </Box>
                    </>
                  )}
                  {aiSummaryStatus === 'complete' && (
                    <>
                      <CheckCircleIcon color="success" sx={{ fontSize: 20, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                        AI Summaries ready — visible in the AI Summary tool
                      </Typography>
                    </>
                  )}
                  {aiSummaryStatus === 'timeout' && (
                    <Typography variant="body2" color="text.secondary">
                      AI Summaries are still generating. Check the AI Summary tool in a few minutes.
                    </Typography>
                  )}
                </Box>
              )}

              {reprocessingResults.school_uuid && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2"><strong>School UUID:</strong> {reprocessingResults.school_uuid}</Typography>
                  <Typography variant="body2"><strong>School Name:</strong> {reprocessingResults.school_name}</Typography>
                  <Typography variant="body2"><strong>Completed:</strong> {new Date(reprocessingResults.reprocessing_completed_at).toLocaleString()}</Typography>
                </Box>
              )}

              {reprocessingResults.updates_applied && Object.keys(reprocessingResults.updates_applied).length > 0 && (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Updates Applied</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      {/* Name Update Summary */}
                      {reprocessingResults.updates_applied.name && (
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                            School Name Update
                          </Typography>
                          <Box sx={{ pl: 2 }}>
                            <Typography variant="body2">
                              <strong>Action:</strong> {reprocessingResults.updates_applied.name.action}
                            </Typography>
                            {reprocessingResults.updates_applied.name.old_name && (
                              <Typography variant="body2">
                                <strong>Previous Name:</strong> {reprocessingResults.updates_applied.name.old_name}
                              </Typography>
                            )}
                            <Typography variant="body2">
                              <strong>New Name:</strong> {reprocessingResults.updates_applied.name.new_name}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Data Year:</strong> {reprocessingResults.updates_applied.name.data_year}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      
                      {/* Location Update Summary */}
                      {(reprocessingResults.updates_applied.address || reprocessingResults.updates_applied.coordinates) && (
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                            Location Update
                          </Typography>
                          <Box sx={{ pl: 2 }}>
                            {reprocessingResults.updates_applied.address && (
                              <>
                                <Typography variant="body2">
                                  <strong>Address:</strong> {reprocessingResults.updates_applied.address.original_address}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Coordinates:</strong> {reprocessingResults.updates_applied.address.geocoded_coordinates.latitude}, {reprocessingResults.updates_applied.address.geocoded_coordinates.longitude}
                                </Typography>
                              </>
                            )}
                            {reprocessingResults.updates_applied.coordinates && (
                              <Typography variant="body2">
                                <strong>Coordinates:</strong> {reprocessingResults.updates_applied.coordinates.latitude}, {reprocessingResults.updates_applied.coordinates.longitude}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      
                      {/* Raw JSON for other updates */}
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                          Raw Update Details
                        </Typography>
                        <pre style={{ fontSize: '0.75rem', overflow: 'auto' }}>
                          {JSON.stringify(reprocessingResults.updates_applied, null, 2)}
                        </pre>
                      </Box>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              {reprocessingResults.service_results && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Service Results</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {Object.entries(reprocessingResults.service_results).map(([serviceKey, result]) => 
                      renderServiceResult(serviceKey, result)
                    )}
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SchoolReprocessor; 